import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Container, Row, Col, Form, Button, Modal, FormGroup, FormControl,
} from "react-bootstrap";
import { motion } from "framer-motion";
import axios from "axios";
import { RiLandscapeLine } from "react-icons/ri";
import { IoArrowForward } from "react-icons/io5";
import "bootstrap/dist/css/bootstrap.min.css";
import "./FormData.css";

const API_BASE = "https://paalyam.jinskadamthodu.com/api/public";

function FormData() {
  const fadeIn = { hidden: { opacity: 0, y: -20 }, visible: { opacity: 1, y: 0 } };

  const [villages, setVillages] = useState([]);     // normalized shape
  const [branches, setBranches] = useState([]);     // normalized shape

  const [loadingVillages, setLoadingVillages] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [errVillages, setErrVillages] = useState("");
  const [errBranches, setErrBranches] = useState("");

  const [formData, setFormData] = useState({
    branch: "",
    district: "",
    taluk: "",
    village: "",
    kara: "",
    old_tp: "",
    old_sy_no: "",
    sub_division_number: "",
    acre: "",
    cents: "",
    east: "",
    west: "",
    north: "",
    south: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ serialNo: "", scheduleNo: "" });
  const [scheduleNumber, setScheduleNumber] = useState("");
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // ---------- helpers: normalize arrays from varying API shapes ----------
  const pickArray = (res) => {
    const d = res?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d?.villages)) return d.villages;
    if (Array.isArray(d?.branches)) return d.branches;
    // sometimes server wraps again
    if (Array.isArray(d?.data?.villages)) return d.data.villages;
    if (Array.isArray(d?.data?.branches)) return d.data.branches;
    return [];
  };

  const normalizeVillages = (arr) =>
    arr.map((it) => ({
      district_id: String(it.district_id ?? it.district?.id ?? ""),
      district_name: it.district_name ?? it.district?.name ?? "",
      taluk_id: String(it.taluk_id ?? it.taluk?.id ?? ""),
      taluk_name: it.taluk_name ?? it.taluk?.name ?? "",
      village_id: String(it.village_id ?? it.id ?? ""),
      village_name: it.village_name ?? it.name ?? "",
    })).filter(v => v.village_id && v.village_name);

  const normalizeBranches = (arr) =>
    arr.map((it) => ({
      id: String(it.id ?? it.branch_id ?? ""),
      branch_code: it.branch_code ?? it.code ?? "",
      branch_name: it.branch_name ?? it.name ?? "",
    })).filter(b => b.id && (b.branch_code || b.branch_name));

  // ---------- fetch villages (independent) ----------
  const fetchVillages = useCallback(async () => {
    try {
      setErrVillages(""); setLoadingVillages(true);
      const res = await axios.get(`${API_BASE}/village`, {
        headers: { Accept: "application/json" },
        timeout: 15000,
      });
      const list = pickArray(res);
      const normalized = normalizeVillages(list);
      if (mountedRef.current) setVillages(normalized);
    } catch (err) {
      console.error("Villages fetch failed", err?.response?.data || err?.message || err);
      if (mountedRef.current) setErrVillages("Failed to load villages.");
    } finally {
      if (mountedRef.current) setLoadingVillages(false);
    }
  }, []);

  // ---------- fetch branches (independent) ----------
  const fetchBranches = useCallback(async () => {
    try {
      setErrBranches(""); setLoadingBranches(true);
      const res = await axios.get(`${API_BASE}/branch`, {
        headers: { Accept: "application/json" },
        timeout: 15000,
      });
      const list = pickArray(res);
      const normalized = normalizeBranches(list);
      if (mountedRef.current) setBranches(normalized);
    } catch (err) {
      console.error("Branches fetch failed", err?.response?.data || err?.message || err);
      if (mountedRef.current) setErrBranches("Failed to load branches.");
    } finally {
      if (mountedRef.current) setLoadingBranches(false);
    }
  }, []);

  // kick off both; one failing won't block the other
  useEffect(() => {
    fetchVillages();
    fetchBranches();
  }, [fetchVillages, fetchBranches]);

  // ---------- schedule number ----------
  const fetchNextScheduleNumber = useCallback(
    async (branchId) => {
      const id = branchId ?? formData.branch;
      if (!id) {
        if (mountedRef.current) setScheduleNumber("");
        return;
      }
      try {
        setLoadingSchedule(true);
        const res = await axios.post(
          `${API_BASE}/land-entry/get-next-schedule-number`,
          { branch_id: id },
          { headers: { "Content-Type": "application/json", Accept: "application/json" }, timeout: 15000 }
        );
        const nextNo =
          res?.data?.data?.next_schedule_number ??
          res?.data?.next_schedule_number ?? "";
        if (mountedRef.current) setScheduleNumber(nextNo || "");
      } catch (err) {
        console.error("Schedule fetch failed", err?.response?.data || err?.message || err);
        if (mountedRef.current) setScheduleNumber("");
      } finally {
        if (mountedRef.current) setLoadingSchedule(false);
      }
    },
    [formData.branch]
  );

  useEffect(() => {
    fetchNextScheduleNumber();
  }, [formData.branch, fetchNextScheduleNumber]);

  // ---------- handlers ----------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };
  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setFormData((p) => ({ ...p, district, taluk: "", village: "" }));
  };
  const handleTalukChange = (e) => {
    const taluk = e.target.value;
    setFormData((p) => ({ ...p, taluk, village: "" }));
  };

  // ---------- derived lists ----------
  const uniqueDistricts = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of villages) {
      const id = v.district_id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name: v.district_name || `District ${id}` });
    }
    return out;
  }, [villages]);

  const uniqueTaluks = useMemo(() => {
    if (!formData.district) return [];
    const d = String(formData.district);
    const seen = new Set();
    const out = [];
    for (const v of villages) {
      if (String(v.district_id) !== d) continue;
      const tid = v.taluk_id;
      if (!tid || seen.has(tid)) continue;
      seen.add(tid);
      out.push({ id: tid, name: v.taluk_name || `Taluk ${tid}` });
    }
    return out;
  }, [villages, formData.district]);

  const uniqueVillages = useMemo(() => {
    if (!formData.taluk) return [];
    const t = String(formData.taluk);
    const seen = new Set();
    const out = [];
    for (const v of villages) {
      if (String(v.taluk_id) !== t) continue;
      const vid = v.village_id;
      if (!vid || seen.has(vid)) continue;
      seen.add(vid);
      out.push({ id: vid, name: v.village_name || `Village ${vid}` });
    }
    return out;
  }, [villages, formData.taluk]);

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.branch || !formData.district || !formData.taluk || !formData.village) {
      alert("Please select Branch, District, Taluk, and Village."); return;
    }
    if (!formData.acre || !formData.cents) {
      alert("Please enter Acre and Cents."); return;
    }

    const payload = {
      branch_id: formData.branch,
      district_id: formData.district,
      taluk_id: formData.taluk,
      village_id: formData.village,
      kara: formData.kara,
      old_tp: formData.old_tp,
      old_sy_no: formData.old_sy_no,
      sub_division_number: formData.sub_division_number,
      acre: formData.acre,
      cents: formData.cents,
      east: formData.east,
      west: formData.west,
      north: formData.north,
      south: formData.south,
    };

    try {
      setSubmitting(true);
      const res = await axios.post(`${API_BASE}/land-entry`, payload, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        timeout: 20000,
      });

      if (res?.data?.success) {
        const serial = res?.data?.data?.serial_number ?? res?.data?.serial_number ?? "N/A";
        const sched = res?.data?.data?.schedule_number ?? res?.data?.schedule_number ?? "N/A";

        setPopupMessage({ serialNo: serial, scheduleNo: sched });
        setShowModal(true);

        // clear entry fields only
        setFormData((p) => ({
          ...p,
          kara: "", old_tp: "", old_sy_no: "", sub_division_number: "",
          acre: "", cents: "", east: "", west: "", north: "", south: "",
        }));

        // refresh schedule for same branch
        await fetchNextScheduleNumber();
      } else {
        alert("Failed to submit data. Please check the form and try again.");
      }
    } catch (err) {
      console.error("Submit failed", err?.response?.data || err?.message || err);
      alert("Failed to submit data. Please check the form and try again.");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const handleClear = () => {
    if (formRef.current) formRef.current.reset();
    setFormData({
      branch: "", district: "", taluk: "", village: "",
      kara: "", old_tp: "", old_sy_no: "", sub_division_number: "",
      acre: "", cents: "", east: "", west: "", north: "", south: "",
    });
    setScheduleNumber("");
    setShowModal(false);
    setPopupMessage({ serialNo: "", scheduleNo: "" });
  };

  return (
    <>
      <section className="form-land">
        <Container className="form-section">
          <div className="left-container">
            <div className="left-icon"><RiLandscapeLine /></div>
            <div className="heading"><h1>Paalyam Family Lands: Record Submission</h1></div>
            <div className="left-btn">Fill the form<span className="btn-icon"><IoArrowForward /></span></div>
          </div>

          <div className="right-form">
            <div className="right-form-heading">
              <p>Ancestral Land</p>
              <h1>Records Submission</h1>
            </div>

            <Form onSubmit={handleSubmit} ref={formRef}>
              <Row className="mb-3">
                <Col md={6} sm={12}>
                  <Form.Group controlId="branch">
                    <Form.Label>Branch</Form.Label>
                    <Form.Select
                      name="branch"
                      value={formData.branch}
                      onChange={handleInputChange}
                      disabled={loadingBranches}
                    >
                      <option value="">{loadingBranches ? "Loading branches..." : "Select Branch"}</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.branch_code ? `${b.branch_code} ` : ""}{b.branch_name}
                        </option>
                      ))}
                    </Form.Select>
                    {errBranches && <small className="text-danger">{errBranches}</small>}
                  </Form.Group>
                </Col>

                <Col md={6} sm={12}>
                  <FormGroup>
                    <Form.Label>Schedule No.</Form.Label>
                    <FormControl
                      type="text"
                      value={loadingSchedule ? "Loading..." : (scheduleNumber || "—")}
                      readOnly
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4} sm={12}>
                  <Form.Group controlId="district">
                    <Form.Label>District</Form.Label>
                    <Form.Select
                      name="district"
                      value={formData.district}
                      onChange={handleDistrictChange}
                      disabled={loadingVillages}
                    >
                      <option value="">{loadingVillages ? "Loading districts..." : "Select District"}</option>
                      {uniqueDistricts.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </Form.Select>
                    {errVillages && <small className="text-danger">{errVillages}</small>}
                  </Form.Group>
                </Col>

                <Col md={4} sm={12}>
                  <Form.Group controlId="taluk">
                    <Form.Label>Taluk</Form.Label>
                    <Form.Select
                      name="taluk"
                      value={formData.taluk}
                      onChange={handleTalukChange}
                      disabled={!formData.district || loadingVillages}
                    >
                      <option value="">{!formData.district ? "Select District first" : "Select Taluk"}</option>
                      {uniqueTaluks.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4} sm={12}>
                  <Form.Group controlId="village">
                    <Form.Label>Village</Form.Label>
                    <Form.Select
                      name="village"
                      value={formData.village}
                      onChange={handleInputChange}
                      disabled={!formData.taluk || loadingVillages}
                    >
                      <option value="">{!formData.taluk ? "Select Taluk first" : "Select Village"}</option>
                      {uniqueVillages.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6} sm={12}>
                  <Form.Group controlId="kara">
                    <Form.Label>Kara</Form.Label>
                    <Form.Control type="text" name="kara" value={formData.kara} onChange={handleInputChange} placeholder="Enter Kara" />
                  </Form.Group>
                </Col>
                <Col md={6} sm={12}>
                  <Form.Group controlId="old_tp">
                    <Form.Label>Old TP</Form.Label>
                    <Form.Control type="text" name="old_tp" value={formData.old_tp} onChange={handleInputChange} placeholder="Enter Old TP" />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={3} sm={12}>
                  <Form.Group controlId="old_sy_no">
                    <Form.Label>Old SY No</Form.Label>
                    <Form.Control type="text" name="old_sy_no" value={formData.old_sy_no} onChange={handleInputChange} placeholder="Enter Old SY No" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="sub_division_number">
                    <Form.Label>Sub Division Number</Form.Label>
                    <Form.Control type="text" name="sub_division_number" value={formData.sub_division_number} onChange={handleInputChange} placeholder="Enter Sub Division Number" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="acre">
                    <Form.Label>Acre</Form.Label>
                    <Form.Control type="number" inputMode="decimal" name="acre" value={formData.acre} onChange={handleInputChange} placeholder="Enter Acre" min="0" step="0.01" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="cents">
                    <Form.Label>Cents</Form.Label>
                    <Form.Control type="number" inputMode="decimal" name="cents" value={formData.cents} onChange={handleInputChange} placeholder="Enter Cents" min="0" step="0.01" />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={3} sm={12}>
                  <Form.Group controlId="east">
                    <Form.Label>East</Form.Label>
                    <Form.Control type="text" name="east" value={formData.east} onChange={handleInputChange} placeholder="Enter East" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="south">
                    <Form.Label>South</Form.Label>
                    <Form.Control type="text" name="south" value={formData.south} onChange={handleInputChange} placeholder="Enter South" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="west">
                    <Form.Label>West</Form.Label>
                    <Form.Control type="text" name="west" value={formData.west} onChange={handleInputChange} placeholder="Enter West" />
                  </Form.Group>
                </Col>
                <Col md={3} sm={12}>
                  <Form.Group controlId="north">
                    <Form.Label>North</Form.Label>
                    <Form.Control type="text" name="north" value={formData.north} onChange={handleInputChange} placeholder="Enter North" />
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-center">
                <Button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
                <Button type="button" variant="primary" className="clear-btn" onClick={handleClear} disabled={submitting}>
                  Clear Data
                </Button>
              </div>
            </Form>
          </div>
        </Container>
      </section>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <motion.div initial="hidden" animate="visible" exit="hidden" variants={fadeIn} transition={{ duration: 0.5 }}>
          <Modal.Header closeButton style={{ fontFamily: "Montserrat", borderBottom: "none" }}>
            <Modal.Title style={{ fontFamily: "Montserrat", fontWeight: 600 }}>Success</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontFamily: "Montserrat", color: "#555" }}>
            <p>Form data saved successfully!</p>
            <p>Serial No: <strong>{popupMessage?.serialNo}</strong></p>
            <p>Schedule No: <strong>{popupMessage?.scheduleNo}</strong></p>
          </Modal.Body>
        </motion.div>
      </Modal>
    </>
  );
}

export default FormData;
